import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {WeekdayEnum} from "@/modules/base/enums";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {Player, Team} from "@/modules/team/entity";
import chance from "@/modules/base/services/chance";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import ActionScreener from "@/modules/action-screens/services/ActionScreener";
import PlayerProgressActionScreen from "@/modules/team/components/PlayerProgressActionScreen.vue";
import {AfterSeasonEvaluationEvent} from "@/modules/season/events/AfterSeasonEvaluationEvent";

@injectable()
export class PlayerProgressEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(ActionScreener) private actionScreener: ActionScreener,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterDayEvaluationEvent, callback: this.onAfterDayEvaluationEvent},
      {event: AfterSeasonEvaluationEvent, callback: this.onAfterSeasonEvaluationEvent},
    ];
  }

  async onAfterSeasonEvaluationEvent(event: AfterDayEvaluationEvent) {
    const team = await Team.current();
    const players = await Player
      .createQueryBuilder('player')
      .leftJoin('player.team', 'team')
      .where('team.user = false OR team.id IS NULL')
      .andWhere('player.careerEndedAt IS NULL')
      .getMany();

    await players.forEachAsync(async (player: Player) => {
      const baseSkillProgress = chance.floating({
        min: this.configRepository.get('players.progress.baseMinProgressPerWeek'),
        max: this.configRepository.get('players.progress.baseMaxProgressPerWeek'),
        fixed: 3,
      });

      const talentProgress = baseSkillProgress * player.talent * this.configRepository.get('players.progress.talentFactor');

      player.skill += 12 * (baseSkillProgress + talentProgress);
      await player.save();

      console.debug(`${player.name} progressed`, {baseSkillProgress, talentProgress});
    });
  }

  async onAfterDayEvaluationEvent(event: AfterDayEvaluationEvent): Promise<void> {
    if (event.season.currentDate.weekday() !== WeekdayEnum.TUESDAY) {
      return;
    }

    console.debug(`Start player progress evaluation`);
    const team = await Team.current();
    const players = await Player
      .createQueryBuilder('player')
      .innerJoin('player.team', 'team')
      .where('player.careerEndedAt IS NULL AND team.user = true')
      .getMany();

    await players.forEachAsync(async (player: Player) => {
      const baseSkillProgress = chance.floating({
        min: this.configRepository.get('players.progress.baseMinProgressPerWeek'),
        max: this.configRepository.get('players.progress.baseMaxProgressPerWeek'),
        fixed: 3,
      });

      const talentProgress = baseSkillProgress * player.talent * this.configRepository.get('players.progress.talentFactor');

      const levelBefore = player.level;

      player.skill += baseSkillProgress + talentProgress;
      await player.save();

      console.debug(`${player.name} progressed`, {baseSkillProgress, talentProgress});

      if (levelBefore < player.level) {
        await this.actionScreener.show({
          component: PlayerProgressActionScreen,
          props: {player},
        });
      }
    });

    console.debug(`End player progress evaluation`);
  }
}